/**
 * Entry point. Every provider lives here, nested outside-in, nothing else —
 * `App.tsx` owns the router and the route table, not app bootstrap. Styles
 * still come in first, so the token layer always loads before any component
 * that reads from it, then the one base stylesheet.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Provider as StoreProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { StyledEngineProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import App from './App';
import Loader from './shared/components/Loader';
import { AppThemeProvider } from './theme/AppThemeProvider';
import { queryClient } from './queries/client';
import { persistor, store } from './store';
import './theme/tokens.css';
import './theme/base.css';

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
	<StrictMode>
		<StoreProvider store={store}>
			<PersistGate loading={<Loader />} persistor={persistor}>
				<QueryClientProvider client={queryClient}>
					<StyledEngineProvider injectFirst>
						<AppThemeProvider>
							<LocalizationProvider dateAdapter={AdapterMoment}>
								<App />
							</LocalizationProvider>
						</AppThemeProvider>
					</StyledEngineProvider>
					{import.meta.env.DEV && (
						<ReactQueryDevtools initialIsOpen={false} />
					)}
				</QueryClientProvider>
			</PersistGate>
		</StoreProvider>
	</StrictMode>,
);
